import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { verifyEmail } from '../../services/authService';
import NetworkBackground from '../../components/NetworkBackground';
import styles from './VerifyPage.module.css';

// Status states
const STATUS = {
  VERIFYING: 'verifying',
  SUCCESS: 'success',
  ERROR: 'error',
  INVALID: 'invalid'
};

// Icons as SVG components
const MailIcon = () => (
  <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default function VerifyPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(STATUS.VERIFYING);
  const [countdown, setCountdown] = useState(3);
  const hasVerified = useRef(false);
  const countdownInterval = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus(STATUS.INVALID);
      return;
    }

    if (hasVerified.current) return;
    hasVerified.current = true;

    verifyEmail(token)
      .then(() => {
        setStatus(STATUS.SUCCESS);
        // Start countdown
        let count = 3;
        countdownInterval.current = setInterval(() => {
          count -= 1;
          setCountdown(count);
          if (count === 0) {
            clearInterval(countdownInterval.current);
            navigate('/login');
          }
        }, 1000);
      })
      .catch(() => setStatus(STATUS.ERROR));

    // Cleanup interval on unmount
    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [searchParams, navigate]);

  const getContent = () => {
    switch (status) {
      case STATUS.VERIFYING:
        return {
          iconClass: styles.iconVerifying,
          icon: <div className={styles.spinner} />,
          title: 'Verifying Your Email',
          message: 'Please wait while we verify your email address...'
        };
      case STATUS.SUCCESS:
        return {
          iconClass: styles.iconSuccess,
          icon: <CheckIcon />,
          title: 'Email Verified!',
          message: 'Your email has been verified successfully. You can now log in to your account.'
        };
      case STATUS.ERROR:
        return {
          iconClass: styles.iconError,
          icon: <XIcon />,
          title: 'Verification Failed',
          message: 'The verification link is invalid or has expired. Please request a new verification email.'
        };
      case STATUS.INVALID:
        return {
          iconClass: styles.iconError,
          icon: <MailIcon />,
          title: 'Invalid Link',
          message: 'This verification link is invalid. Please check your email for the correct link.'
        };
      default:
        return null;
    }
  };

  const content = getContent();

  return (
    <div className={styles.pageWrapper}>
      <NetworkBackground />
      <div className={styles.card}>
        <div className={`${styles.iconWrapper} ${content.iconClass}`}>
          {content.icon}
        </div>
        <h1 className={styles.title}>{content.title}</h1>
        <p className={styles.message}>{content.message}</p>
        
        {status === STATUS.SUCCESS && (
          <>
            <Link to="/login" className={styles.loginButton}>
              Go to Login
            </Link>
            <p className={styles.redirectText}>
              Redirecting in {countdown} seconds...
            </p>
          </>
        )}
        
        {(status === STATUS.ERROR || status === STATUS.INVALID) && (
          <Link to="/login" className={styles.retryButton}>
            Back to Login
          </Link>
        )}
      </div>
    </div>
  );
}
